import {
  useSessionAttendeesQuery,
  useSessionEligibleStudentsQuery,
  useSessionInstructorAttendeesQuery,
} from "@/modules/classes/presentation/api/queries";
import { SessionInstructorRoleBadges } from "@/modules/classes/presentation/ui/SessionInstructorRoleBadges";
import { parseYearMonthFromDateYyyyMmDd } from "@/modules/reports/domain/lib/parse-year-month";
import { useInstructorPayrollBatchByMonthQuery } from "@/modules/reports/presentation/api/queries";
import {
  Button,
  Checkbox,
  Input,
  ScrollArea,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { Loader2, SearchIcon, Undo2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  useBulkCheckInClassSessionMutation,
  useBulkUndoCheckInClassSessionMutation,
} from "../api/queries";
import { AddGuestInstructorDialog } from "./AddGuestInstructorDialog";
import { AddSubstituteInstructorDialog } from "./AddSubstituteInstructorDialog";

type CheckInClassFormProps = {
  sessionId: string;
  sessionStatus?: "scheduled" | "completed" | "cancelled";
  capacity: number;
  attendanceCount: number;
  instructorIds: string[];
  instructorNames: string[];
  /** Preferred: roles from session.instructors */
  instructors?: Array<{
    instructorId: string;
    name: string;
    role: "lead" | "assist";
  }>;
  /** YYYY-MM-DD — soft-lock instructor check-in when payroll closed */
  sessionDate?: string;
  onSuccess: () => void;
  onCheckInComplete?: () => void;
  onCancel: () => void;
};

function formatCheckedInAt(checkedInAt: string): string {
  try {
    const d = new Date(checkedInAt);
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h < 12 ? "AM" : "PM";
    const hour = h % 12 || 12;
    return `Checked-in at ${hour.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${ampm}`;
  } catch {
    return checkedInAt;
  }
}

export function CheckInClassForm({
  sessionId,
  sessionStatus = "scheduled",
  capacity,
  attendanceCount,
  instructorIds,
  instructorNames,
  instructors: instructorsProp,
  sessionDate,
  onSuccess,
  onCheckInComplete,
  onCancel,
}: CheckInClassFormProps) {
  const [search, setSearch] = useState("");
  const [searchForApi, setSearchForApi] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<
    Set<string>
  >(new Set());

  const eligibleStudentsQuery = useSessionEligibleStudentsQuery(
    sessionId,
    searchForApi,
  );
  const attendeesQuery = useSessionAttendeesQuery(sessionId);
  const instructorAttendeesQuery =
    useSessionInstructorAttendeesQuery(sessionId);
  const [selectedUndoAttendanceIds, setSelectedUndoAttendanceIds] = useState<
    Set<string>
  >(new Set());
  const [selectedUndoInstructorIds, setSelectedUndoInstructorIds] = useState<
    Set<string>
  >(new Set());
  const [substituteDialogOpen, setSubstituteDialogOpen] = useState(false);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);

  const bulkCheckInMutation = useBulkCheckInClassSessionMutation();
  const bulkUndoCheckInMutation = useBulkUndoCheckInClassSessionMutation();
  const isSubmitting =
    bulkCheckInMutation.isPending || bulkUndoCheckInMutation.isPending;

  const yearMonth = useMemo(() => {
    if (!sessionDate) return null;
    try {
      return parseYearMonthFromDateYyyyMmDd(sessionDate);
    } catch {
      return null;
    }
  }, [sessionDate]);
  const payrollBatchQuery = useInstructorPayrollBatchByMonthQuery(
    yearMonth?.year ?? null,
    yearMonth?.month ?? null,
  );
  const payrollLocked = !!payrollBatchQuery.data;
  const instructorActionsDisabled = isSubmitting || payrollLocked;

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearch(val);
    setSearchForApi(val);
  }, 300);

  const students = eligibleStudentsQuery.data?.students ?? [];
  const partnerNoStudentCheckIn =
    eligibleStudentsQuery.data?.studentCheckInEnabled === false;
  const isCompletedSession = sessionStatus === "completed";
  const instructorOnlyMode = partnerNoStudentCheckIn || isCompletedSession;
  const attendees = attendeesQuery.data?.attendees ?? [];
  const instructorAttendees = instructorAttendeesQuery.data?.attendees ?? [];
  const instructors =
    instructorsProp && instructorsProp.length > 0
      ? instructorsProp.map((i) => ({
        id: i.instructorId,
        name: i.name,
        role: i.role as "lead" | "assist" | undefined,
      }))
      : instructorIds.map((id, i) => ({
        id,
        name: instructorNames[i] ?? "",
        role: undefined as "lead" | "assist" | undefined,
      }));
  const instructorsNotCheckedIn = instructors.filter(
    (inst) => !instructorAttendees.some((a) => a.instructorId === inst.id),
  );
  const capacityPercent =
    capacity > 0 ? Math.round((attendanceCount / capacity) * 100) : 0;

  const toggleStudent = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(students.map((s) => s.id)));
  }, [students]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedInstructorIds(new Set());
  }, []);

  const toggleInstructor = useCallback((id: string) => {
    setSelectedInstructorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleUndoAttendance = useCallback((id: string) => {
    setSelectedUndoAttendanceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleUndoInstructor = useCallback((id: string) => {
    setSelectedUndoInstructorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkUndo = useCallback(async () => {
    const attendanceIds = instructorOnlyMode
      ? []
      : Array.from(selectedUndoAttendanceIds);
    const instructorCheckInIds = Array.from(selectedUndoInstructorIds);

    if (attendanceIds.length === 0 && instructorCheckInIds.length === 0) {
      toast.error("ກະລຸນາເລືອກນັກຮຽນ ຫຼື ຄູສອນຢ່າງໜ້ອຍ 1 ຄົນ");
      return;
    }

    try {
      const result = await bulkUndoCheckInMutation.mutateAsync({
        sessionId,
        attendanceIds: attendanceIds.length > 0 ? attendanceIds : [],
        instructorCheckInIds:
          instructorCheckInIds.length > 0 ? instructorCheckInIds : undefined,
      });

      const studentSuccessCount = result.studentSucceeded?.length ?? 0;
      const studentFailCount = result.studentFailed?.length ?? 0;
      const instructorSuccessCount = result.instructorSucceeded?.length ?? 0;
      const instructorFailCount = result.instructorFailed?.length ?? 0;

      const totalSuccess = studentSuccessCount + instructorSuccessCount;
      const totalFail = studentFailCount + instructorFailCount;

      if (totalSuccess > 0) {
        setSelectedUndoAttendanceIds(new Set());
        setSelectedUndoInstructorIds(new Set());
        const messages: string[] = [];
        if (studentSuccessCount > 0) {
          messages.push(`ນັກຮຽນ ${studentSuccessCount} ຄົນ`);
        }
        if (instructorSuccessCount > 0) {
          messages.push(`ຄູສອນ ${instructorSuccessCount} ຄົນ`);
        }
        toast.success(`ຍົກເລີກການກວດຊື່ສຳເລັດ: ${messages.join(", ")}`);
        onSuccess();
      }

      if (totalFail > 0) {
        const failMessages: string[] = [];
        if (studentFailCount > 0) {
          failMessages.push(`ນັກຮຽນ ${studentFailCount} ຄົນ`);
        }
        if (instructorFailCount > 0) {
          failMessages.push(`ຄູສອນ ${instructorFailCount} ຄົນ`);
        }
        toast.error(`ຍົກເລີກການກວດຊື່ບໍ່ສຳເລັດ: ${failMessages.join(", ")}`);
      }
    } catch {
      // Error shown by mutation onError
    }
  }, [
    sessionId,
    instructorOnlyMode,
    selectedUndoAttendanceIds,
    selectedUndoInstructorIds,
    bulkUndoCheckInMutation,
    onSuccess,
  ]);

  const handleBulkSave = useCallback(async () => {
    const studentIds = instructorOnlyMode ? [] : Array.from(selectedIds);
    const instructorIdsArray = Array.from(selectedInstructorIds);

    if (studentIds.length === 0 && instructorIdsArray.length === 0) {
      toast.error(
        instructorOnlyMode
          ? "ກະລຸນາເລືອກຄູສອນຢ່າງໜ້ອຍ 1 ຄົນ"
          : "ກະລຸນາເລືອກນັກຮຽນ ຫຼື ຄູສອນຢ່າງໜ້ອຍ 1 ຄົນ",
      );
      return;
    }

    try {
      const result = await bulkCheckInMutation.mutateAsync({
        sessionId,
        studentIds: studentIds.length > 0 ? studentIds : [],
        instructorIds:
          instructorIdsArray.length > 0 ? instructorIdsArray : undefined,
      });

      const studentSuccessCount = result.succeeded.length;
      const studentFailCount = result.failed.length;
      const instructorSuccessCount = result.instructorSucceeded?.length ?? 0;
      const instructorFailCount = result.instructorFailed?.length ?? 0;

      const totalSuccess = studentSuccessCount + instructorSuccessCount;
      const totalFail = studentFailCount + instructorFailCount;

      if (totalSuccess > 0) {
        setSelectedIds(new Set());
        setSelectedInstructorIds(new Set());
        const messages: string[] = [];
        if (studentSuccessCount > 0) {
          messages.push(`ນັກຮຽນ ${studentSuccessCount} ຄົນ`);
        }
        if (instructorSuccessCount > 0) {
          messages.push(`ຄູສອນ ${instructorSuccessCount} ຄົນ`);
        }
        toast.success(`ກວດຊື່ສຳເລັດ: ${messages.join(", ")}`);
        if (onCheckInComplete) onCheckInComplete();
        else onSuccess();
      }

      if (totalFail > 0) {
        const failMessages: string[] = [];
        if (studentFailCount > 0) {
          failMessages.push(`ນັກຮຽນ ${studentFailCount} ຄົນ`);
        }
        if (instructorFailCount > 0) {
          failMessages.push(`ຄູສອນ ${instructorFailCount} ຄົນ`);
        }
        toast.error(
          `ກວດຊື່ບໍ່ສຳເລັດ: ${failMessages.join(", ")} (ອາດແພັກເກດໝົດອາຍຸ ຫຼື ບໍ່ສາມາດໃຊ້ໄດ້)`,
        );
      }
    } catch {
      // Error shown by mutation onError
    }
  }, [
    sessionId,
    instructorOnlyMode,
    selectedIds,
    selectedInstructorIds,
    bulkCheckInMutation,
    onSuccess,
    onCheckInComplete,
  ]);

  return (
    <div className="flex h-full flex-col gap-4 sm:flex-row">
      {/* Left: Student Search + Package status */}
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {partnerNoStudentCheckIn ? (
          <p className="rounded-md border border-border bg-muted/40 p-4 text-muted-foreground text-sm">
            ຄລາດສ໌ນີ້ເປັນໂຄງການ Partner (instructor-only) — ບໍ່ຮອງຮັບການເຊັກຊື່ນັກຮຽນ ຫຼື ການຕັດເຄຣດິດແພັກເກດ. ຍັງເຊັກຊື່ຄູສອນໄດ້ຕາມປົກກະຕິ.
          </p>
        ) : isCompletedSession ? (
          <p className="rounded-md border border-border bg-muted/40 p-4 text-muted-foreground text-sm">
            Session ນີ້ສຳເລັດແລ້ວ — ສາມາດກວດຊື່ຄູສອນເທົ່ານັ້ນ
          </p>
        ) : null}

        {!instructorOnlyMode && (
          <>
            <div>
              <label
                htmlFor="search"
                className="mb-1.5 block font-medium text-muted-foreground text-sm"
              >
                ຄົ້ນຫານັກຮຽນ
              </label>
              <div className="relative">
                <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="ຊື່ ຫຼື ລະຫັດສະມາຊິກ"
                  className="border-primary/50 pl-9 focus-visible:ring-primary"
                  defaultValue={search}
                  onChange={(e) => {
                    debouncedSearch(e.target.value);
                  }}
                />
              </div>
            </div>

            {students.length > 0 && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-sm">
                    ເລືອກນັກຮຽນທີ່ມາຮອດ ({students.length} ຄົນ)
                  </span>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={selectAll}
                    >
                      ເລືອກທັງໝົດ
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={clearSelection}
                    >
                      ລ້າງການເລືອກ
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-[280px] rounded-md border border-border">
                  <ul className="space-y-0 p-2">
                    {students.map((s) => (
                      <li key={s.id}>
                        <label
                          htmlFor={`check-${s.id}`}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-muted"
                        >
                          <Checkbox
                            id={`check-${s.id}`}
                            checked={selectedIds.has(s.id)}
                            onCheckedChange={() => toggleStudent(s.id)}
                            disabled={isSubmitting}
                          />
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground text-sm">
                            {(s.name ?? "").slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium">{s.name}</div>
                            <div className="text-muted-foreground text-xs">
                              #{s.memberId ?? s.id.slice(-5)}
                            </div>
                          </div>
                        </label>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </>
            )}

            {students.length === 0 && !eligibleStudentsQuery.isLoading && (
              <p className="rounded-md border border-border p-4 text-center text-muted-foreground text-sm">
                {instructorsNotCheckedIn.length > 0
                  ? "ນັກຮຽນກວດຊື່ຄົບແລ້ວ — ກະລຸນາເລືອກຄູສອນດ້ານຂວາ ແລ້ວກົດບັນທຶກ"
                  : "ບໍ່ມີນັກຮຽນທີ່ສາມາດກວດຊື່ໄດ້ ຫຼື ກວດຊື່ຄົບແລ້ວ"}
              </p>
            )}

            <div className="mt-auto flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-sm">
                ເລືອກແລ້ວ: ນັກຮຽນ {selectedIds.size} ຄົນ
                {selectedInstructorIds.size > 0 &&
                  `, ຄູສອນ ${selectedInstructorIds.size} ຄົນ`}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onCancel}>
                  ຍົກເລີກ
                </Button>
                <Button
                  onClick={handleBulkSave}
                  disabled={
                    (selectedIds.size === 0 &&
                      selectedInstructorIds.size === 0) ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      ກຳລັງບັນທຶກ...
                    </>
                  ) : (
                    "ບັນທຶກ"
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        {instructorOnlyMode && (
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel}>
              ຍົກເລີກ
            </Button>
            <Button
              onClick={handleBulkSave}
              disabled={
                selectedInstructorIds.size === 0 ||
                isSubmitting ||
                payrollLocked
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  ກຳລັງບັນທຶກ...
                </>
              ) : isCompletedSession ? (
                "ຢືນຢັນກວດຊື່ຄູ"
              ) : (
                "ບັນທຶກ"
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Right: Instructor check-in + Attendees + Capacity bar */}
      <div className="flex w-full min-w-0 flex-col gap-4 border-t pt-4 sm:w-80 sm:shrink-0 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-6">
        {instructors.length > 0 && (
          <div className="min-w-0">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="font-medium">ຄູສອນ</h3>
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-1.5 font-medium text-xs">
                  {instructorAttendees.length}/{instructors.length}
                </span>
              </div>
              {instructorsNotCheckedIn.length > 0 && !payrollLocked && (
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      setSelectedInstructorIds(
                        new Set(instructorsNotCheckedIn.map((i) => i.id)),
                      );
                    }}
                  >
                    ເລືອກທັງໝົດ
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      setSelectedInstructorIds(new Set());
                    }}
                  >
                    ລ້າງ
                  </Button>
                </div>
              )}
            </div>
            {payrollLocked ? (
              <p className="mb-2 text-amber-800 text-xs dark:text-amber-200">
                ປິດງວດ payroll ແລ້ວ — ບໍ່ສາມາດເພີ່ມ/ຍົກເລີກການກວດຊື່ຄູໄດ້
              </p>
            ) : null}
            {!payrollLocked ? (
              <div className="mb-2 space-y-2">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 min-w-0 flex-1 text-xs"
                    onClick={() => setSubstituteDialogOpen(true)}
                  >
                    + ເລືອກຄູສອນແທນ
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 min-w-0 flex-1 text-xs"
                    onClick={() => setGuestDialogOpen(true)}
                  >
                    + ເພີ່ມຄູສອນຊົ່ວຄາວ
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  ສອນແທນ / ຄູຊົ່ວຄາວ: ຄິດຄ່າຈ້າງແບບຄູຫຼັກ (ບໍ່ແມ່ນ TA). ຕ້ອງເພີ່ມຄູຊ່ວຍ ໃນໜ້າແກ້ຕາຕະລາງ.
                </p>
              </div>
            ) : null}
            <div className="space-y-2">
              {instructorAttendees.length > 0 && !payrollLocked && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">
                    ເລືອກເພື່ອຍົກເລີກການກວດຊື່
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        setSelectedUndoInstructorIds(
                          new Set(instructorAttendees.map((a) => a.id)),
                        );
                      }}
                    >
                      ເລືອກທັງໝົດ
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        setSelectedUndoInstructorIds(new Set());
                      }}
                    >
                      ລ້າງ
                    </Button>
                  </div>
                </div>
              )}
              <ScrollArea className="max-h-[min(280px,40vh)] rounded-md border border-border">
                <div className="space-y-2 p-2">
                  {instructorAttendees.map((a) => (
                    <label
                      key={a.id}
                      htmlFor={`undo-instructor-${a.id}`}
                      className="flex cursor-pointer gap-2 rounded-md border border-green-500/30 bg-green-500/5 px-2 py-2 text-sm hover:bg-green-500/10"
                    >
                      <Checkbox
                        id={`undo-instructor-${a.id}`}
                        className="mt-0.5 shrink-0"
                        checked={selectedUndoInstructorIds.has(a.id)}
                        onCheckedChange={() => toggleUndoInstructor(a.id)}
                        disabled={instructorActionsDisabled}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-sm">
                            {a.instructorName}
                          </span>
                          <SessionInstructorRoleBadges
                            role={a.instructorRole}
                            isSubstitute={a.isSubstitute}
                          />
                        </div>
                        <span className="text-muted-foreground text-xs">
                          {formatCheckedInAt(a.checkedInAt)}
                        </span>
                      </div>
                    </label>
                  ))}
                  {instructorsNotCheckedIn.length > 0 && (
                    <ul className="space-y-0">
                      {instructorAttendees.length > 0 && (
                        <li className="py-1">
                          <span className="text-muted-foreground text-xs">
                            ຍັງບໍ່ໄດ້ກວດຊື່
                          </span>
                        </li>
                      )}
                      {instructorsNotCheckedIn.map((inst) => (
                        <li key={inst.id}>
                          <label
                            htmlFor={`check-instructor-${inst.id}`}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-2 hover:bg-muted"
                          >
                            <Checkbox
                              id={`check-instructor-${inst.id}`}
                              className="shrink-0"
                              checked={selectedInstructorIds.has(inst.id)}
                              onCheckedChange={() => toggleInstructor(inst.id)}
                              disabled={instructorActionsDisabled}
                            />
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground text-xs">
                              {(inst.name ?? "").slice(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <div className="truncate font-medium text-sm">
                                  {inst.name}
                                </div>
                                <SessionInstructorRoleBadges role={inst.role} />
                              </div>
                            </div>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  {instructorAttendees.length === 0 &&
                    instructorsNotCheckedIn.length === 0 && (
                      <p className="py-2 text-center text-muted-foreground text-xs">
                        ບໍ່ມີຄູສອນ
                      </p>
                    )}
                </div>
              </ScrollArea>
              {(selectedUndoInstructorIds.size > 0 ||
                selectedInstructorIds.size > 0) && (
                  <span className="text-muted-foreground text-xs">
                    ເລືອກແລ້ວ
                    {selectedUndoInstructorIds.size > 0 &&
                      ` ຍົກເລີກ ${selectedUndoInstructorIds.size} ຄົນ`}
                    {selectedInstructorIds.size > 0 &&
                      ` ກວດຊື່ ${selectedInstructorIds.size} ຄົນ`}
                  </span>
                )}
            </div>
          </div>
        )}
        {!instructorOnlyMode && (
          <div className="h-full">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="font-medium">ຜູ້ເຂົ້າຮຽນປະຈຸບັນ</h3>
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-1.5 font-medium text-xs">
                  {attendees.length}
                </span>
              </div>
              {attendees.length > 0 && (
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      setSelectedUndoAttendanceIds(
                        new Set(attendees.map((a) => a.id)),
                      );
                    }}
                  >
                    ເລືອກທັງໝົດ
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      setSelectedUndoAttendanceIds(new Set());
                    }}
                  >
                    ລ້າງ
                  </Button>
                </div>
              )}
            </div>
            <ScrollArea className="rounded-md border">
              <ul className="h-full p-2">
                {attendees.length === 0 ? (
                  <li className="py-4 text-center text-muted-foreground text-sm">
                    ຍັງບໍ່ມີຜູ້ເຂົ້າຮຽນ
                  </li>
                ) : (
                  attendees.map((a) => (
                    <li key={a.id}>
                      <label
                        htmlFor={`undo-attendance-${a.id}`}
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`undo-attendance-${a.id}`}
                          checked={selectedUndoAttendanceIds.has(a.id)}
                          onCheckedChange={() => toggleUndoAttendance(a.id)}
                          disabled={isSubmitting}
                        />
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground text-xs">
                          {(a.studentName ?? "").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-sm">
                            {a.studentName}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {formatCheckedInAt(a.checkedInAt)}
                          </div>
                        </div>
                      </label>
                    </li>
                  ))
                )}
              </ul>
            </ScrollArea>
            {(selectedUndoAttendanceIds.size > 0 ||
              selectedUndoInstructorIds.size > 0) && (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">
                    ເລືອກແລ້ວ: ນັກຮຽນ {selectedUndoAttendanceIds.size} ຄົນ
                    {selectedUndoInstructorIds.size > 0 &&
                      `, ຄູສອນ ${selectedUndoInstructorIds.size} ຄົນ`}
                  </span>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="gap-1.5"
                    onClick={handleBulkUndo}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ກຳລັງຍົກເລີກ...
                      </>
                    ) : (
                      <>
                        <Undo2Icon className="h-3.5 w-3.5" />
                        ຍົກເລີກການກວດຊື່
                      </>
                    )}
                  </Button>
                </div>
              )}
          </div>
        )}

        {instructorOnlyMode && selectedUndoInstructorIds.size > 0 && (
          <div className="mt-2 flex items-center justify-end gap-2">
            <span className="text-muted-foreground text-xs">
              ເລືອກຍົກເລີກ: ຄູ {selectedUndoInstructorIds.size} ຄົນ
            </span>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="gap-1.5"
              onClick={handleBulkUndo}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ກຳລັງຍົກເລີກ...
                </>
              ) : (
                <>
                  <Undo2Icon className="h-3.5 w-3.5" />
                  ຍົກເລີກການກວດຊື່ຄູ
                </>
              )}
            </Button>
          </div>
        )}

        <div className="mt-auto">
          {instructorOnlyMode ? (
            <>
              <div className="mb-1 flex justify-between text-sm">
                <span className="text-muted-foreground">ຄູທີ່ເຊັກຊື່:</span>
                <span className="font-medium">
                  {instructorAttendees.length} / {instructors.length}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{
                    width: `${instructors.length > 0
                        ? Math.min(
                          100,
                          Math.round(
                            (instructorAttendees.length /
                              instructors.length) *
                            100,
                          ),
                        )
                        : 0
                      }%`,
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div className="mb-1 flex justify-between text-sm">
                <span className="text-muted-foreground">ຄວາມຈຸຂອງຊົ່ວໂມງຮຽນ:</span>
                <span className="font-medium">
                  {attendees.length} / {capacity} ນັກຮຽນ
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{
                    width: `${Math.min(100, capacityPercent)}%`,
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <AddSubstituteInstructorDialog
        sessionId={sessionId}
        sessionType="general_class"
        open={substituteDialogOpen}
        onOpenChange={setSubstituteDialogOpen}
        onSuccess={onSuccess}
        assignedInstructorIds={instructorIds}
        assignedInstructorNames={instructorNames}
      />
      <AddGuestInstructorDialog
        sessionId={sessionId}
        sessionType="general_class"
        open={guestDialogOpen}
        onOpenChange={setGuestDialogOpen}
        onSuccess={onSuccess}
        assignedInstructorIds={instructorIds}
        assignedInstructorNames={instructorNames}
      />
    </div>
  );
}
